/**
 * Kept versions of an artifact and what changed between them.
 *
 * `reviseArtifact` keeps the version it replaces as a bounded revision (the
 * last few versions, each payload capped), and records a small structured
 * diff of the readable text: which lines were rewritten (the words around the
 * change, what they said before and after) and how many were added or
 * removed. The diff is deterministic and never paraphrases, so the viewer can
 * show "$26k → $31k" exactly as the two versions wrote it.
 *
 * Isomorphic and dependency-free, like the envelope module that re-exports it.
 */

import { splitSentences } from "../text/sentences.ts";

/** How many earlier versions an artifact keeps (the current one is extra). */
export const ARTIFACT_REVISION_LIMIT = 4;
/** An earlier version keeps its payload only when it serialises within this. */
export const ARTIFACT_REVISION_PAYLOAD_CHARS = 24_000;
/** Rewritten lines kept per change; the rest are only counted. */
export const ARTIFACT_CHANGE_EDIT_LIMIT = 3;

const EDIT_TEXT_CHARS = 160;
const CONTEXT_WORDS = 6;
const MAX_UNITS = 400;
const UNIT_CHARS = 400;
const PAIRING_SIMILARITY = 0.5;
const REASON_CHARS = 200;
const IGNORED_KEYS = /^(?:_id|id|artifactId|createdAt|createdDate|updatedAt|retrievedAt|version|revision)$/;

/** One rewritten line: the words around the change and what they said before and after. */
export type ArtifactEdit = {
  lead?: string;
  before: string;
  after: string;
  trail?: string;
};

/** What changed from the previous version's readable text. */
export type ArtifactChange = {
  /** Up to {@link ARTIFACT_CHANGE_EDIT_LIMIT} rewritten lines, in reading order. */
  edits: ArtifactEdit[];
  /** Rewritten lines beyond the kept edits. */
  moreEdits: number;
  /** Lines only the new version has. */
  added: number;
  /** Lines only the old version had. */
  removed: number;
};

/** An earlier version of an artifact, kept so the viewer can step back to it. */
export type ArtifactRevision<T = unknown> = {
  version: number;
  /** When that version was produced. */
  revisedAt: number;
  /** The machine description of the transform that produced that version. */
  transform?: string;
  /** Why that version was made, as a clause ("Grafana changed its prices"). */
  reason?: string;
  /** What that version changed from its own predecessor. */
  change?: ArtifactChange;
  /** Absent when it was larger than {@link ARTIFACT_REVISION_PAYLOAD_CHARS}. */
  payload?: T;
  payloadOmitted?: boolean;
};

const clip = (text: string, limit: number): string => (
  text.length <= limit ? text : `${text.slice(0, Math.max(1, limit - 1)).trimEnd()}…`
);

const stripCitationMarkers = (text: string): string => text
  .replace(/\s*\[\d+\]/g, "")
  .replace(/\s+([.,;:!?])/g, "$1");

/** Splits prose into sentences and at line breaks, whitespace collapsed. */
function sentencesOf(text: string): string[] {
  return splitSentences(text).map((unit) => unit.replace(/\s+/g, " ").trim()).filter(Boolean);
}

function leavesOf(value: unknown, key: string, out: string[], depth: number): void {
  if (out.length >= MAX_UNITS || depth > 6) return;
  if (typeof value === "string") {
    if (!value.trim()) return;
    sentencesOf(stripCitationMarkers(value)).forEach((sentence) => {
      if (out.length < MAX_UNITS) out.push(key ? `${key}: ${sentence}` : sentence);
    });
    return;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    out.push(key ? `${key}: ${String(value)}` : String(value));
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => leavesOf(item, key, out, depth + 1));
    return;
  }
  if (value && typeof value === "object") {
    Object.keys(value as Record<string, unknown>).forEach((childKey) => {
      if (IGNORED_KEYS.test(childKey)) return;
      leavesOf((value as Record<string, unknown>)[childKey], childKey, out, depth + 1);
    });
  }
}

/**
 * The readable lines of a payload: the sentences of its prose body when it has
 * one (citation markers removed, so renumbered sources are not a change), else
 * every leaf value as "field: value".
 */
export function readableUnitsOf(payload: unknown): string[] {
  const units: string[] = [];
  if (typeof payload === "string") {
    sentencesOf(stripCitationMarkers(payload)).forEach((sentence) => units.push(sentence));
  } else if (payload && typeof payload === "object" && !Array.isArray(payload)
    && typeof (payload as { body?: unknown }).body === "string") {
    const record = payload as { title?: unknown; body: string };
    if (typeof record.title === "string" && record.title.trim()) units.push(record.title.trim());
    sentencesOf(stripCitationMarkers(record.body)).forEach((sentence) => units.push(sentence));
  } else {
    leavesOf(payload, "", units, 0);
  }
  return units.slice(0, MAX_UNITS).map((unit) => unit.slice(0, UNIT_CHARS));
}

const wordsOf = (text: string): string[] => text.split(/\s+/).filter(Boolean);

function similarity(a: string, b: string): number {
  const left = wordsOf(a.toLowerCase());
  const right = wordsOf(b.toLowerCase());
  if (left.length === 0 || right.length === 0) return 0;
  let shared = 0;
  const pool = right.slice();
  left.forEach((word) => {
    const at = pool.indexOf(word);
    if (at >= 0) {
      shared += 1;
      pool.splice(at, 1);
    }
  });
  return shared / Math.max(left.length, right.length);
}

/** The changed words of a rewritten line, with a few words of context either side. */
export function editBetween(before: string, after: string): ArtifactEdit {
  const a = wordsOf(before);
  const b = wordsOf(after);
  let prefix = 0;
  while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) prefix += 1;
  let suffix = 0;
  while (
    suffix < a.length - prefix
    && suffix < b.length - prefix
    && a[a.length - 1 - suffix] === b[b.length - 1 - suffix]
  ) suffix += 1;
  const leadWords = a.slice(Math.max(0, prefix - CONTEXT_WORDS), prefix);
  const trailWords = a.slice(a.length - suffix, a.length - suffix + CONTEXT_WORDS);
  const edit: ArtifactEdit = {
    before: clip(a.slice(prefix, a.length - suffix).join(" "), EDIT_TEXT_CHARS),
    after: clip(b.slice(prefix, b.length - suffix).join(" "), EDIT_TEXT_CHARS),
  };
  if (leadWords.length > 0) edit.lead = `${prefix > CONTEXT_WORDS ? "…" : ""}${leadWords.join(" ")}`;
  if (trailWords.length > 0) edit.trail = `${trailWords.join(" ")}${suffix > CONTEXT_WORDS ? "…" : ""}`;
  return edit;
}

type Op = { kind: "same" | "removed" | "added"; text: string };

/** Line diff by longest common subsequence; inputs are capped at {@link MAX_UNITS}. */
function diffUnits(before: string[], after: string[]): Op[] {
  const rows = before.length;
  const cols = after.length;
  const table: number[][] = [];
  for (let i = 0; i <= rows; i += 1) {
    table.push(new Array<number>(cols + 1).fill(0));
  }
  for (let i = rows - 1; i >= 0; i -= 1) {
    for (let j = cols - 1; j >= 0; j -= 1) {
      table[i][j] = before[i] === after[j]
        ? table[i + 1][j + 1] + 1
        : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < rows && j < cols) {
    if (before[i] === after[j]) {
      ops.push({ kind: "same", text: before[i] });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      ops.push({ kind: "removed", text: before[i] });
      i += 1;
    } else {
      ops.push({ kind: "added", text: after[j] });
      j += 1;
    }
  }
  while (i < rows) ops.push({ kind: "removed", text: before[i++] });
  while (j < cols) ops.push({ kind: "added", text: after[j++] });
  return ops;
}

/**
 * What changed between two payloads' readable text. Rewritten lines (a
 * removed line and an added line in the same place that share most words)
 * become edits; everything else is counted.
 */
export function describeArtifactChange(previous: unknown, next: unknown): ArtifactChange {
  const ops = diffUnits(readableUnitsOf(previous), readableUnitsOf(next));
  const change: ArtifactChange = { edits: [], moreEdits: 0, added: 0, removed: 0 };
  let index = 0;
  while (index < ops.length) {
    if (ops[index].kind === "same") {
      index += 1;
      continue;
    }
    const removed: string[] = [];
    const added: string[] = [];
    while (index < ops.length && ops[index].kind !== "same") {
      (ops[index].kind === "removed" ? removed : added).push(ops[index].text);
      index += 1;
    }
    const pairs = Math.min(removed.length, added.length);
    let paired = 0;
    for (let pair = 0; pair < pairs; pair += 1) {
      if (similarity(removed[pair], added[pair]) < PAIRING_SIMILARITY) break;
      paired += 1;
      if (change.edits.length < ARTIFACT_CHANGE_EDIT_LIMIT) {
        change.edits.push(editBetween(removed[pair], added[pair]));
      } else {
        change.moreEdits += 1;
      }
    }
    change.removed += removed.length - paired;
    change.added += added.length - paired;
  }
  return change;
}

function boundedPayload<T>(payload: T): { payload?: T; payloadOmitted?: boolean } {
  if (payload === undefined) return { payloadOmitted: true };
  try {
    const json = JSON.stringify(payload);
    if (json !== undefined && json.length <= ARTIFACT_REVISION_PAYLOAD_CHARS) return { payload };
  } catch {
    // An unserialisable payload is kept as metadata only.
  }
  return { payloadOmitted: true };
}

/** Clips a producer-given reason to its stored bound; blank reads as none. */
export function normalizeRevisionReason(reason: unknown): string | undefined {
  if (typeof reason !== "string") return undefined;
  const trimmed = reason.replace(/\s+/g, " ").trim();
  return trimmed ? clip(trimmed, REASON_CHARS) : undefined;
}

type RevisableArtifact<T> = {
  version: number;
  payload: T;
  payloadOmitted?: boolean;
  provenance: {
    retrievedAt: number;
    transform?: string;
    reason?: string;
    change?: ArtifactChange;
  };
  revisions?: ArtifactRevision<T>[];
};

/** The revision an artifact becomes when a newer version replaces it. */
export function revisionOf<T>(artifact: RevisableArtifact<T>): ArtifactRevision<T> {
  const kept = artifact.payloadOmitted ? { payloadOmitted: true } : boundedPayload(artifact.payload);
  const revision: ArtifactRevision<T> = {
    version: artifact.version,
    revisedAt: artifact.provenance.retrievedAt,
    ...kept,
  };
  if (artifact.provenance.transform) revision.transform = artifact.provenance.transform;
  if (artifact.provenance.reason) revision.reason = artifact.provenance.reason;
  if (artifact.provenance.change) revision.change = artifact.provenance.change;
  return revision;
}

/**
 * The kept history after `artifact` is replaced: its own snapshot first, then
 * its earlier revisions, newest first, capped at {@link ARTIFACT_REVISION_LIMIT}.
 */
export function historyAfterRevising<T>(artifact: RevisableArtifact<T>): ArtifactRevision<T>[] {
  const earlier = Array.isArray(artifact.revisions) ? artifact.revisions : [];
  return [revisionOf(artifact)]
    .concat(earlier.filter((revision) => revision && revision.version < artifact.version))
    .slice(0, ARTIFACT_REVISION_LIMIT);
}
